import React, { useState } from "react"
import { PlayCircle, ExternalLink } from "lucide-react"
import { proxiedImageUrl, needsImageProxy } from "../utils"
import Thumbnail from "./Thumbnail"
import { useAnalytics, ANALYTICS_EVENTS } from "../../../analytics"

/**
 * Instagram reels are played inline via the browser's native <video> element,
 * sourced from `video.url` (the reel's instagram.com/reel/<shortcode>/
 * permalink - confirmed against real API responses to actually serve a
 * playable stream, unlike `video_url` which wasn't panning out). No extra
 * package needed for playback.
 *
 * `video.thumbnail_url` is used two ways: as the <video>'s `poster` (shown
 * immediately, before/while the stream loads) and, if the video itself fails
 * to load, as the fallback preview via `Thumbnail` - which has its own
 * hotlink-protection-aware proxy + placeholder chain (Thumbnail.jsx), so a
 * reel that can't play at all still shows a real thumbnail image wherever
 * possible rather than a blank box.
 *
 * In that fallback, only the small "Watch on Instagram" pill actually
 * navigates out - clicking the rest of the tile (same as clicking the
 * title/info below it elsewhere in the UI) calls `onOpen` to open the
 * in-app detail view instead, so a reel whose video can't play isn't the one
 * spot in the grid that unexpectedly redirects you away from the site.
 */
const ReelPlayer = ({ video, style, onOpen }) => {
    const { trackEvent } = useAnalytics()
    // Two different reasons end up at the same fallback UI: the API simply
    // didn't send a `url` for this reel (a data gap, not a frontend bug), vs.
    // one being sent but the browser failing to load it as a <video> source
    // (the `error` event on <video>). Logged distinctly so this is
    // diagnosable from the console instead of every reel that falls back
    // looking like the same unexplained failure.
    const [failed, setFailed] = useState(() => {
        if (!video.url) {
            console.warn(`[UGC Explorer] Reel ${video.video_id || video.shortcode} has no url from the API - showing the thumbnail fallback.`)
            return true
        }
        return false
    })

    if (failed) {
        return (
            <div style={{ ...style, position: "absolute" }}>
                <Thumbnail
                    src={video.thumbnail_url}
                    alt={video.title}
                    isInstagram
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
                />
                {/* The tile itself opens the detail view (same as clicking
                    the card's title/info elsewhere) - only the pill below is
                    an actual link out to Instagram. */}
                <div
                    onClick={onOpen}
                    style={{
                        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
                        alignItems: "center", justifyContent: "center", gap: "10px",
                        background: "rgba(0,0,0,0.45)", color: "#fff",
                        cursor: onOpen ? "pointer" : "default",
                    }}
                >
                    <PlayCircle size={46} style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.5))" }} />
                    <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => {
                            e.stopPropagation()
                            trackEvent(ANALYTICS_EVENTS.FEATURE.UGC_OUTBOUND_CLICKED, {
                                video_id: video.video_id,
                                platform: video.platform,
                                link_type: "watch",
                            })
                        }}
                        style={{
                            display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: 700,
                            background: "rgba(0,0,0,0.6)", padding: "6px 12px", borderRadius: "999px",
                            color: "#fff", textDecoration: "none",
                        }}
                    >
                        Watch on Instagram <ExternalLink size={13} />
                    </a>
                </div>
            </div>
        )
    }

    return (
        <video
            src={video.url}
            poster={(needsImageProxy(video.thumbnail_url) ? proxiedImageUrl(video.thumbnail_url) : video.thumbnail_url) || undefined}
            controls
            playsInline
            preload="metadata"
            onError={(e) => {
                console.warn(`[UGC Explorer] Reel ${video.video_id || video.shortcode}'s url failed to load as a video source - falling back to the thumbnail.`, video.url, e?.target?.error)
                setFailed(true)
            }}
            style={style}
        />
    )
}

export default ReelPlayer
